import React, { useContext, useEffect, useState, useRef } from "react";
import { Form, OverlayTrigger, Table, Tooltip, Modal, Button, Popover } from "react-bootstrap";
import { useTranslation, withTranslation } from "react-i18next";
import { BiPencil } from "react-icons/bi";
import { BsArrowReturnRight, BsThreeDotsVertical, BsFileEarmarkFill, BsX, BsDownload } from "react-icons/bs";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FcFinePrint } from "react-icons/fc";
import { toast } from "react-toastify";
import { LoaderContext, RegulationContext } from "../../../../App";
import StandardService from "../../../../core/service/StandardService";
import '../DataView.scss';
import ConfirmationModal from "../../../../components/ConfirmationModal/ConfirmationModal";
import Utils from "../../../../core/utilities/Utils";
import { CompareCategoryType } from "../../../../core/constants/common";

const QualitativeDataView = ({ t, filterData, view, viewType, columnHideShow }) => {

    const { glbRegulation } = useContext(RegulationContext);
    const glbRegulationId = glbRegulation?.id ?? "";
    const { setLoaderDisplay } = useContext(LoaderContext);
    const [filter, setFilter] = useState(filterData);
    const [qualitativeData, setQualitativeData] = useState([]);
    const [qnAnsData, setQnAnsData] = useState({});
    const [topicVisibility, setTopicVisibility] = useState({});
    const [qnVisibility, setQnVisibility] = useState({});
    const [answer, setAnswer] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState({});
    const [selectedQn, setSelectedQn] = useState({});
    const [subQnVisibility, setSubQnVisibility] = useState({});
    const [subQnAnsData, setSubQnAnsData] = useState({});
    const [branchesFromFilteredData, setBranchesFromFilteredData] = useState([]);
    const [isEdit, setIsEdit] = useState(view == 'EDIT_VIEW' ? true : false);

    const [selectedQnCom, setSelectedQnCom] = useState({});
    const [showCommentsModal, setShowCommentsModal] = useState(false);
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [qnComments, setQnComments] = useState('');
    const [fileUploader, setFileUploader] = useState('');
    const [selectedFile, setSelectedFile] = useState({});
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [messageConfirmation, setMessageConfirmation] = useState('');
    const [isCommentEdit, setIsCommentEdit] = useState(view == 'EDIT_VIEW' ? true : false);
    const [tempComments, setTempComments] = useState('');
    const [tempAttachments, setTempAttachments] = useState([]);
    const fileInputRef = useRef();
    const [colHideShow, setColHideShow] = useState({
        codes: true,
        uom: true
    });
    const { k, i18n } = useTranslation();
    useEffect(() => {
        if (!glbRegulationId) return;
        getQualitativeData(filterData);
        setFilter(filterData);
    }, [glbRegulationId, filterData]);

    useEffect(() => {
        setIsEdit(view == 'EDIT_VIEW' ? true : false)
        setIsCommentEdit(view == 'EDIT_VIEW' ? true : false)
    }, [view]);

    useEffect(() => {
        let branches = [];
        let order = filterData.businessUnitIds;
        for (let index = 0; index < qualitativeData?.length; index++) {
            const qData = qualitativeData[index];
            if (qData?.metricConsolidated?.length > 0) {
                branches = qData?.metricConsolidated[0].branchConsolidated;
                break;
            }
        }
        branches.sort((a, b) => {
            return order.indexOf(a.branchId) - order.indexOf(b.branchId);
        });
        setBranchesFromFilteredData(branches);
    }, [qualitativeData]);

    useEffect(() => {
        setColHideShow(columnHideShow);
    }, [columnHideShow]);

    const getQualitativeData = (f) => {  
        let order = f.businessUnitIds;
        setLoaderDisplay(true);
        StandardService.getQualitativeDataByFilter(glbRegulationId, f.reportingPeriod, f)
            .then(res => {
                setLoaderDisplay(false);
                if (res.status !== 200) return;
                let result = res.data.data.topics ? res.data.data.topics : [];
                if (result.length > 0) {
                    result.forEach(element => {
                        if (element.metricConsolidated.length > 0) {
                            element.metricConsolidated.forEach(metrics => {
                                metrics['branchConsolidated'].sort((a, b) => {
                                    return order.indexOf(a.branchId) - order.indexOf(b.branchId);
                                });
                            })
                        }
                    });
                };
                const transformedData = result.map((obj) => transformObject(obj)); 
                setQualitativeData(transformedData);

                // If some metric was already in expanded state,
                // then keep its state unchanged unless the topic has changed in the new filter
                let previousTopicExists = false;
                for (let i = 0; i < f.topicIds?.length; i++) {
                    const topicId = f.topicIds[i];
                    if (filter.topicIds?.indexOf(topicId) < 0) continue;

                    previousTopicExists = true;
                    break;
                }
                let qnVisibilityData = JSON.parse(JSON.stringify(qnVisibility));
                if (!previousTopicExists && f.topicIds?.length > 0) {
                    qnVisibilityData = {};
                }
                setQnVisibility(qnVisibilityData);
                // Refresh questions data for the displayed questions
                const metricIds = Object.keys(qnVisibilityData);
                if (metricIds?.length == 0) {
                    setQnAnsData({});
                }
                metricIds?.forEach(metricId => {
                    if (!qnVisibilityData[metricId] || qnVisibilityData[metricId] === 'hide') return;
                    getQuestionAnswers(+metricId, f);
                });

            }).catch(err => {
                setLoaderDisplay(false);
            });
    };

    const transformObject = (obj) => {
        const transformedObj = { ...obj };
        // Add the translated values to the main object based on languageCode
        if (obj.localizedContents && obj.localizedContents.length > 0) {
            obj.localizedContents.forEach((item) => {
                if (item.fieldName === 'name') {
                    transformedObj[item.languageCode] = item.translatedValue;
                }
            });
        }
        // Recursively process the children objects
        if (obj.metricConsolidated && obj.metricConsolidated.length > 0) {
            transformedObj.metricConsolidated = obj.metricConsolidated.map((child) => transformObject(child));
        }

        return transformedObj;
    };

    const getQuestionAnswers = (metricId, f) => {
       
        let order = f.businessUnitIds;
        setLoaderDisplay(true);
        StandardService.getQuestionAnswers(metricId, f.reportingPeriod, f)
            .then((res) => {
                setLoaderDisplay(false);
                if (res.status !== 200) return;
                let result = res.data.data.questions;
                if (result.length > 0) {
                    result.forEach(element => {
                        element['branchConsolidated'].sort((a, b) => {
                            return order.indexOf(a.branchId) - order.indexOf(b.branchId);
                        });
                        if (element.subQuestions.length > 0) {
                            element.subQuestions.forEach(subelement => {
                                subelement['branchConsolidated'].sort((a, b) => {
                                    return order.indexOf(a.branchId) - order.indexOf(b.branchId);
                                });
                            });
                        }
                    });
                }
                setQnAnsData(existingQns => {
                    const transformedQuestions = result.map(transformQuestionObject);
                    existingQns[metricId] = transformedQuestions.length > 0 ? transformedQuestions : result;
                    return { ...existingQns };
                });
                
                // setQnAnsData(existingQns => {
                //     existingQns[metricId] = [];
                //     existingQns[metricId] = result;
                //     return existingQns;
                // });
                const vis = { ...qnVisibility };
                vis[metricId] = 'show';
                setQnVisibility(vis);
            })
            .catch((e) => {
                setLoaderDisplay(false);
            });
    };
    const transformQuestionObject = (question) => {
        const transformed = { ...question };
    
        if (question.localizedContents?.length > 0) {
            question.localizedContents.forEach(item => {
                if (item.fieldName === 'question') {
                    transformed[item.languageCode] = item.translatedValue;
                }
            });
        }
    
        if (question.subQuestions?.length > 0) {
           
            transformed.subQuestions = question.subQuestions.map(subQn => {
                const transformedSub = { ...subQn };
                if (subQn.localizedContents?.length > 0) {
                    subQn.localizedContents.forEach(item => {
                        if (item.fieldName === 'question') {
                            transformedSub[item.languageCode] = item.translatedValue;
                        }
                    });
                }
                
                return transformedSub;
            });
        }
    
        return transformed;
    };
    

    const renderTableHeader = () => {
        return (
            <thead>
                <tr>
                    <th className="">{t("Topics.label")}</th>
                    {colHideShow?.codes && <th className="text-center">Code</th>}
                    {/* <th className="text-center total-col-header">
						<span className="mx-1">Answers</span>
					</th> */}
                    {
                        branchesFromFilteredData?.map((branch, bIndex) => {
                            return (
                                <th key={`branchHeader-${bIndex}`} className="branch-cell text-center 1">
                                    <span className="mx-1">{branch.branchName}</span>
                                </th>
                            );
                        })
                    }
                </tr>
            </thead>
        );
    };

    const renderEmptyDataLabel = () => {
        return (
            <tr>
                <td colSpan="4" className="text-center 2">
                    <div className="Nodatafound_box">
                        <h6><FcFinePrint className="me-2" />{t("Nodatafound.label")}</h6>
                    </div>
                </td>
            </tr>
        )
    };

    const renderTopicRow = (topic, index) => {
        const topicColSpanValue = branchesFromFilteredData?.length ? (branchesFromFilteredData.length + 2) : 2;
        return (
            <tr key={`topic-${topic.topicId}-${index}`} className="topic-row">
                <td colSpan={topicColSpanValue} className="name-cell">
                    <span className="DataTopic_sticky cursor"
                        onClick={() => {
                            showOrHideTopic(topic.topicId)
                        }}>
                        <span className="left_arrow_head" >
                            {(topicVisibility[topic.topicId] === 'hide') ? <FaChevronDown /> : <FaChevronUp />}
                        </span>
                        <span>
                        {(i18n.language && topic[i18n.language]) ? topic[i18n.language] : topic.topicName}
                            {/* {topic.topicName} */}
                            </span>
                    </span>
                </td>
            </tr>
        );
    };

    const renderMetricRow = (metric, index, topicId) => {
        return (
            <tr key={`metric-${metric.metricId}-${index}`}
                className={topicVisibility[topicId] === 'hide' ? 'metric-row d-none' : 'metric-row'}>
                <td className="name-cell">
                    
                    <div className="cursor" onClick={() => showOrHideQnAns(metric.metricId)}>
                        {
                            metric.questionCount > 0 &&
                            <span className="cursor left_arrow" >
                                {qnVisibility[metric.metricId] === 'show' ? <FaChevronUp /> : <FaChevronDown />}
                            </span>
                        }
                        <span className="data_show_view01">
                        {(i18n.language && metric[i18n.language]) ? metric[i18n.language] : metric.metricName }
                            {/* {metric.metricName} */}
                            </span>
                    </div>
                </td>
                {colHideShow?.codes && <td className="text-center">{metric.metricCode}</td>}
                {/* <td className="total-cell"></td> */}
                {
                    metric.branchConsolidated?.map((branch, bIndex) => {
                        return (
                            <td key={`metric-branch-cell-${bIndex}`} className="branch-cell parent-branch-cell">
                                {branch.answers}
                                {(view == 'EDIT_VIEW' && metric.questionCount > 0) &&
                                    <span className="hideEditQual" onClick={() => { showQnAns(metric.metricId) }}>
                                        <BiPencil />
                                    </span>
                                }
                            </td>
                        );
                    })
                }
            </tr>
        );
    };

    const renderQnAnsRows = (metricId) => {
        if (!qnAnsData[metricId]) return <></>;

        return (
            qnAnsData[metricId]?.map((qnAns, dIndex) => {
                return (
                    <>
                        <tr key={`dp-${qnAns.questionId}-${dIndex}`} className="dp-row">
                            <td className="name-cell">
                                {/* {
                                    qnAns.subQuestions.length > 0 &&
                                    <span className="mx-2 cursor" onClick={() => showOrHideSubQnAns(qnAns)}>
                                        {subQnVisibility[qnAns.questionId] === 'show' ? <FaChevronUp /> : <FaChevronDown />}
                                    </span>
                                } */}
                                <span className="data_show_view01">
                                    {/* {qnAns.questionText} */}
                                    {qnAns[i18n.language] || qnAns.questionText}
                                    </span>
                            </td>
                            {colHideShow?.codes && <td className="text-center">-</td>}
                            {/* <td className="total-cell">{qnAns.answers.length > 0 ? qnAns.answers[0].answer : ''}</td> */}
                            {
                                qnAns.branchConsolidated?.map((branch, bIndex) => {
                                    return renderQnAnsBranchCell(branch, bIndex, qnAns);
                                })
                            }
                        </tr>
                        {
                            qnAns.subQuestions.length > 0 &&
                            <>
                                {renderSubQnAnsRows(qnAns)}
                            </>
                        }
                    </>
                )
            })
        );
    };

    const renderQnAnsBranchCell = (branch, index, question) => {
        return (
            <td key={`dataPoint-branch-cell-${index}`} className="branch-cell child-branch-cell edit_branchCell cursor 2" >
                <div className="qus_boxAns" onClick={() => showEditAnswers(branch, question)}>
                    {
                        (branch.answers.length > 0 && branch.answers[0].answer.length < 25) &&
                        <span className='text-muted mx-1'>{branch.answers.length > 0 ? branch.answers[0].answer : ''}</span>
                    }
                    {
                        (branch.answers.length > 0 && branch.answers[0].answer.length > 25) &&
                        <span className='text-muted'>{branch.answers.length > 0 ? branch.answers[0].answer.substring(0, 25) + '...' : ''}</span>
                        // <OverlayTrigger placement="top"
                        //     overlay={renderAnswerTooltip(branch.answers.length > 0 ? branch.answers[0].answer : '')}>
                        //     <span className='text-muted mx-1'>{branch.answers.length > 0 ? branch.answers[0].answer.substring(0, 25) + '...' : ''}</span>
                        // </OverlayTrigger>
                    }
                </div>
                {
                    (branch.answers.length > 0 && (branch.answers[0].answer != '' || branch.answers[0].remark || branch.answers[0].attachments?.length > 0)) &&
                    <OverlayTrigger
                        placement="auto"
                        overlay={<Tooltip id="tooltip-like">  {t("CommentsandDocumentsFile.label")}</Tooltip>} >
                        <span className="commAtt cursor" onClick={() => showCommentsAndUploads(question, branch)}>
                            <BsThreeDotsVertical />
                        </span>
                    </OverlayTrigger>
                }
            </td>
        );
    };

    const renderSubQnAnsRows = (qnAns) => {
        // if (!subQnAnsData[questionId]) return <></>;

        return (
            qnAns.subQuestions?.map((subQnAns, dIndex) => {
                return (
                    <tr key={`dp-${subQnAns.questionId}-${dIndex}`} className="dp-row">
                        <td className="name-cell">
                            <span className="data_show_view01"><BsArrowReturnRight />
                             {/* {subQnAns.questionText} */}
                             {subQnAns[i18n.language] || subQnAns.questionText}

                            </span>
                        </td>
                        {colHideShow?.codes && <td className="text-center">-</td>}
                        {/* <td className="total-cell">{subQnAns.answers.length > 0 ? subQnAns.answers[0].answer : ''}</td> */}
                        {
                            subQnAns.branchConsolidated?.map((branch, bIndex) => {
                                return renderSubQnAnsBranchCell(branch, bIndex, subQnAns);
                            })
                        }
                    </tr>
                )
            })
        );
    };

    const renderSubQnAnsBranchCell = (branch, index, subQuestion) => {
        return (
            <td key={`dataPoint-branch-cell-${index}`} className="branch-cell child-branch-cell edit_branchCell cursor 1" >
                <div className="qus_boxAns" onClick={() => showEditAnswers(branch, subQuestion)}>
                    {
                        (branch.answers.length > 0 && branch.answers[0].answer.length < 25) &&
                        <span className='text-muted mx-1'>{branch.answers.length > 0 ? branch.answers[0].answer : ''}</span>
                    }
                    {
                        (branch.answers.length > 0 && branch.answers[0].answer.length > 25) &&
                        <span className='text-muted mx-1'>{branch.answers.length > 0 ? branch.answers[0].answer.substring(0, 25) + '...' : ''}</span>
                        // <OverlayTrigger placement="top"
                        //     overlay={renderAnswerTooltip(branch.answers.length > 0 ? branch.answers[0].answer : '')}>
                        //     <span className='text-muted mx-1'>{branch.answers.length > 0 ? branch.answers[0].answer.substring(0, 25) + '...' : ''}</span>
                        // </OverlayTrigger>
                    }
                </div>

                {
                    (branch.answers.length > 0 && (branch.answers[0].answer != '' || branch.answers[0].remark || branch.answers[0].attachments?.length > 0)) &&
                    <OverlayTrigger
                        placement="auto"
                        overlay={<Tooltip id="tooltip-like">  {t("CommentsandDocumentsFile.label")}</Tooltip>} >
                        <span className="commAtt cursor" onClick={() => showCommentsAndUploads(subQuestion, branch)}>
                            <BsThreeDotsVertical />
                        </span>
                    </OverlayTrigger>

                }
            </td>
        );
    };

    const showQnAns = (metricId) => {
        const vis = { ...qnVisibility };
        vis[metricId] = 'show';
        setQnVisibility(vis);
        getQuestionAnswers(metricId, filterData);
    };

    const showOrHideQnAns = (metricId) => {
        const vis = { ...qnVisibility };
        vis[metricId] = vis[metricId] === 'show' ? 'hide' : 'show';
        setQnVisibility(vis);
        if (vis[metricId] === 'hide') {
            const existingQAs = JSON.parse(JSON.stringify(qnAnsData));
            existingQAs[metricId] = [];
            setQnAnsData(existingQAs);
            return;
        }
        getQuestionAnswers(metricId, filterData);
    };

    const showOrHideSubQnAns = (question) => {
        const vis = { ...subQnVisibility };
        vis[question.questionId] = vis[question.questionId] === 'show' ? 'hide' : 'show';
        setSubQnVisibility(vis);
        if (vis[question.questionId] === 'hide') {
            const existingSubQAs = JSON.parse(JSON.stringify(subQnAnsData));
            existingSubQAs[question.questionId] = [];
            setSubQnAnsData(existingSubQAs);
            return;
        }
        const existingSubQAs = JSON.parse(JSON.stringify(subQnAnsData));
        existingSubQAs[question.questionId] = question.subQuestions;
        setSubQnAnsData(existingSubQAs);
    };

    const showOrHideTopic = (topicId) => {
        const vis = { ...topicVisibility };
        vis[topicId] = vis[topicId] === 'hide' ? 'show' : 'hide';
        setTopicVisibility(vis);
    };

    const saveQualMetricAnswers = () => {
        const ans = selectedBranch.answers.length > 0 ? selectedBranch.answers[0] : []
        const payload = [{
            id: ans.id > 0 ? ans.id : null,
            questionId: selectedQn.questionId,
            answer: answer,
            branchId: selectedBranch.branchId
        }];
        setLoaderDisplay(true);
        StandardService.postQualMetricAnswers(filter?.reportingPeriod, payload)
            .then(res => {
                setLoaderDisplay(false);
                handleModalClose();
                toast.success("Success", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                getQualitativeData(filterData);
            }).catch(err => {
                setLoaderDisplay(false);
            });
    }

    const showEditAnswers = (branch, question) => {
        setShowModal(true);
        setSelectedBranch(branch);
        setAnswer(branch.answers.length > 0 ? branch.answers[0].answer : '');
        setSelectedQn({
            questionCode: question.questionCode,
            questionId: question.questionId,
            questionText: question.questionText
        });
    }

    const handleModalClose = () => {
        setShowModal(false);
        setSelectedBranch({});
        setSelectedQn({});
        setAnswer('');
        setIsEdit(view == 'EDIT_VIEW' ? true : false);
    }

    const renderAnswerTooltip = (ans) => {
        return (
            // <Tooltip className="mytooltip">
            //     {ans}
            // </Tooltip>
            <Popover
                id="popover-basic"
                placement="top"
                positionLeft={200}
                positionTop={50}
                title="Answer">
                {ans}
            </Popover>
        );
    }

    const showCommentsAndUploads = (question, branch) => {
        setShowCommentsModal(true);
        let data = {
            questionId: question.questionId,
            branchId: branch.branchId,
            id: branch.answers.length > 0 ? branch.answers[0].id : null,
            remark: branch.answers.length > 0 ? branch.answers[0].remark : '',
            attachments: branch.answers.length > 0 ? branch.answers[0].attachments : [],
            answer: branch.answers.length > 0 ? branch.answers[0].answer : ''
        }
        setQnComments(branch.answers.length > 0 ? branch.answers[0].remark : '');
        setAttachedFiles(branch.answers.length > 0 ? branch.answers[0].attachments : []);
        setSelectedQnCom(data);
    }

    const handleCommentsModalClose = () => {
        setShowCommentsModal(false);
        setIsCommentEdit(view == 'EDIT_VIEW' ? true : false);
        setMessageConfirmation('');
        setSelectedQnCom({});
        setQnComments('');
        setAttachedFiles([]);
        setSelectedFile({});
        setTempComments('');
        setTempAttachments([]);
    }

    const uploadAttachments = (fileUploaded) => {
        if (fileUploaded) {
            const formData = new FormData();
            formData.append("document", fileUploaded);
            setLoaderDisplay(true);
            StandardService.uploadAttachments(formData).then(res => {
                setLoaderDisplay(false);
                if (res.status === 200 || res.status === 201) {
                    let files = tempAttachments;
                    files.push(res.data)
                    // setAttachedFiles(files);
                    setTempAttachments(files);
                    // toast.success("Document uploaded", {
                    // 	position: toast.POSITION.TOP_RIGHT,
                    // });
                    fileInputRef.current.value = null;
                }
            }).catch((error) => {
                setLoaderDisplay(false);
            });
        } else {
            toast.warning('Please select a file');
        }
    }

    const handleFileSelect = (event) => {
        if (event.target.files[0].size <= 1024 * 1024 * 2) {
            const fileUploaded = event.target.files[0];
            uploadAttachments(fileUploaded);
        } else {
            fileInputRef.current.value = null;
            toast.warning('Upload files less than 2MB', {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 3000
            });
        }
    };

    const saveCommentsAndFiles = () => {
        let existData = selectedQnCom;
        existData['remark'] = tempComments
        existData['attachments'] = tempAttachments;
        setSelectedQnCom(existData);
        setLoaderDisplay(true);
        StandardService.saveBranchCommentsAttachments(
            filter.reportingPeriod,
            existData
        ).then(res => {
            setLoaderDisplay(false);
            if (res.status !== 200) return;
            toast.success("Success", {
                position: toast.POSITION.TOP_RIGHT,
            });
            handleCommentsModalClose();
            getQualitativeData(filterData);
        }).catch((error) => {
            setLoaderDisplay(false);
            handleCommentsModalClose();
        });
    }

    const noAttachmentLabel = () => {
        return (
            <div>
                <FcFinePrint className="me-2" /> No attachments available!
            </div>
        )
    };

    const showAttachedFiles = () => {
        return (
            <>
                <div className="p-1">
                    <label className='mb-1'><b>	{t("Comments.label")}</b></label>
                    {
                        qnComments &&
                        <p>{qnComments}</p>
                    }
                    {
                        !qnComments &&
                        <p><FcFinePrint className="me-2" /> No comments added</p>
                    }
                </div>
                <div className="p-1">
                    <label className='mb-1'><b>{t("Documents&File.label")}</b></label>
                    <div className='attachedFiles_box p-1 5'>
                        {attachedFiles?.length === 0 && noAttachmentLabel()}
                        {
                            attachedFiles.map((files, index) => {
                                return (
                                    <div className="attachedFiles_boxIn">
                                        <span className="attach_icon"><BsFileEarmarkFill /></span>
                                        {files.name ? files.name : 'No name'}
                                        <span className="attach_Closeicon" onClick={() => downloadAttchement(files)}>
                                            <BsDownload />
                                        </span>
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>
            </>
        );
    }

    const showConfirm = (data) => {
        setMessageConfirmation(t("DeleteFileConfirmationMessage.label"));
        setSelectedFile(data)
        setShowConfirmation(true);
    }

    const confirmDelete = (val) => {
        setShowConfirmation(false);
        if (val) {
            deleteFile(selectedFile.id);
        }
    };

    const deleteFile = (id) => {
        const index = tempAttachments.findIndex(x => x.id === id);
        if (index >= 0) {
            tempAttachments.splice(index, 1);
            let files = JSON.stringify(tempAttachments)
            setTempAttachments(JSON.parse(files));
        }
    }

    const downloadAttchement = (data) => {
        if (data.externalId) {
            setLoaderDisplay(true);
            StandardService.getDocumentsAttached(data.externalId)
                .then((response) => {
                    setLoaderDisplay(false);
                    let fetchDataModified = `data:${response.data.mimeType};base64,${response.data.file}`;
                    let link = document.createElement("a");
                    link.href = fetchDataModified;
                    link.download = response.data.name;
                    link.click();
                })
                .catch((error) => {
                    setLoaderDisplay(false);
                    console.log(error);
                });
        } else {
            downloadAttchementAlternative(data.id);
        }
    }

    const downloadAttchementAlternative = (id) => {
        setLoaderDisplay(true);
        StandardService.getDocumentsAttachedAlt(id)
            .then((response) => {
                setLoaderDisplay(false);
                let fetchDataModified = `data:${response.data.mimeType};base64,${response.data.file}`;
                let link = document.createElement("a");
                link.href = fetchDataModified;
                link.download = response.data.name;
                link.click();
            })
            .catch((error) => {
                setLoaderDisplay(false);
                console.log(error);
            });
    }

    const setupEdit = () => {
        setIsCommentEdit(view == 'EDIT_VIEW' ? true : false);
        let files = JSON.stringify(attachedFiles)
        setTempAttachments(JSON.parse(files));
        setTempComments(qnComments);
    }

    const cancelEdit = () => {
        setIsCommentEdit(view == 'EDIT_VIEW' ? true : false);
        setTempAttachments([]);
        setTempComments('');
        fileInputRef.current.value = null;
    }

    const renderCategoryRow = () => {
        return (
            <tr key={`category`} className="cat-row">
                <td colSpan={2} className="name-cell 1">
                    <span className="DataCat_sticky">
                        <span className="text-primary"><b>{Utils.toTitleCase(CompareCategoryType[filter.categoryType])}</b></span>
                    </span>
                </td>
            </tr>
        );
    };

    return (
        <div>
            <div className="table-holder table-responsive mt-0">
                <Table responsive="sm" bordered >
                    {renderTableHeader()}
                    <tbody>
                        {renderCategoryRow()}
                        {qualitativeData?.length === 0 && renderEmptyDataLabel()}
                        {
                            qualitativeData?.length > 0 &&
                            qualitativeData?.map((topic, tIndex) => {
                                return (
                                    <>
                                        {renderTopicRow(topic, tIndex)}
                                        {
                                            topic.metricConsolidated?.length > 0 &&
                                            topic.metricConsolidated?.map((metric, mIndex) => {
                                                return (
                                                    <>
                                                {/* <h4>here</h4> */}
                                                        {renderMetricRow(metric, mIndex, topic.topicId)}
                                                        {renderQnAnsRows(metric.metricId)}
                                                    </>
                                                );
                                            })
                                        }
                                    </>
                                );
                            })
                        }
                    </tbody>
                </Table>

                <Modal show={showModal}
                    onHide={handleModalClose}
                    size="lg"
                    aria-labelledby="contained-modal-title-vcenter"
                    centered backdrop="static"
                    keyboard={false}>
                    <Modal.Header closeButton>
                         <Modal.Title as="h5">{selectedBranch.branchName}</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <div className='p-1'>
                            <label className='mb-1'><b>{selectedQn.questionText}</b></label>
                            {
                                isEdit &&
                                <textarea className='form-control' rows="10" value={answer} placeholder="Provide your answer here"
                                    onChange={(e) => { setAnswer(e.target.value) }}></textarea>
                            }
                            {
                                !isEdit &&
                                <p>{answer ? answer : 'No answer provided'}</p>
                            }
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => handleModalClose()}>
                            {t("Cancel.label")}
                        </Button>
                        {
                            isEdit && view == 'EDIT_VIEW' &&
                            <Button variant="primary" className="sbmBtn" onClick={() => saveQualMetricAnswers()}>
                                {t("Save.label")}
                            </Button>
                        }
                        {/* {
                            !isEdit && view != 'EDIT_VIEW' &&
                            <Button variant="primary" className="sbmBtn" onClick={() => setIsEdit(true)}>
                                {t("Edit.label")}
                            </Button>
                        } */}
                    </Modal.Footer>
                </Modal>

                {showConfirmation && (
                    <ConfirmationModal
                        modalShow={true}
                        messageText={messageConfirmation}
                        callbackModal={confirmDelete}
                        confirmBtn={t("Confirm.label")}
                        CancelBtn={t("Cancel.label")}
                    />
                )}

                <Modal show={showCommentsModal}
                    onHide={handleCommentsModalClose}
                    size="lg"
                    aria-labelledby="contained-modal-title-vcenter"
                    centered backdrop="static"
                    keyboard={false}>
                    <Modal.Header closeButton>
                        <Modal.Title as="h5">
                            {t("CommentsandDocumentsFile.label")}
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        {isCommentEdit &&
                            <div>
                                <div className='p-1'>
                                    <label className='mb-1'>
                                        {t("Comments.label")}
                                    </label>
                                    <textarea className='form-control' rows="5" value={tempComments} placeholder="Provide your comments here"
                                        onChange={(e) => { setTempComments(e.target.value) }}></textarea>
                                </div>
                                <div className='p-1'>
                                    <label className='mb-1'>Upload files (less than 2MB)</label>
                                    <div className='d-flex'>
                                        <input
                                            className='form-control'
                                            type="file"
                                            id="imageUpload"
                                            accept=".pdf, .docx, .png, .jpg, .xls, .xlsx"
                                            onChange={(e) => handleFileSelect(e)}
                                            onClick={(e) => {
                                                e.target.value = null;
                                            }}
                                            ref={fileInputRef}
                                        />
                                        {/* <Button variant="primary" className="sbmBtn ms-2" onClick={() => { uploadAttachments() }}>
										{t("Upload.label")}
									</Button> */}
                                    </div>
                                    <div className='attachedFiles_box p-1 6'>
                                        {
                                            tempAttachments.map((files, index) => {
                                                return (
                                                    <div className="attachedFiles_boxIn">
                                                        <span className="attach_icon"><BsFileEarmarkFill /></span>
                                                        {files.name}
                                                        <span className="attach_Closeicon " onClick={() => showConfirm(files)}>
                                                            <BsX />
                                                        </span>
                                                    </div>
                                                );
                                            })
                                        }
                                    </div>
                                </div>
                            </div>
                        }

                        {!isCommentEdit &&
                            showAttachedFiles()
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        {
                            isCommentEdit &&
                            <>
                                <Button variant="secondary" onClick={() => handleCommentsModalClose()}>
                                    {t("Cancel.label")}
                                </Button>
                                <Button variant="primary" className="sbmBtn" onClick={() => saveCommentsAndFiles()}>
                                    {t("Update.label")}
                                </Button>
                            </>
                        }
                        {
                            !isCommentEdit &&
                            <>
                                <Button variant="secondary" onClick={() => handleCommentsModalClose()}>
                                    {t("Close.label")}
                                </Button>
                                {/* <Button variant="primary" className="sbmBtn" onClick={() => setupEdit()}>
                                    {t("Edit.label")}
                                </Button> */}
                            </>
                        }
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    )
};

export default withTranslation()(QualitativeDataView);